// @ts-check
import { annualDebtService, mortgageBalanceAfterYears } from "./amortization";
import { clampMin0, round2, num } from "./validators";

function stressTestRatePct(contractRatePct, cfg) {
  return Math.max(contractRatePct + cfg.conservative.stressTestBuffer, cfg.conservative.stressTestFloorRate);
}

/**
 * ⚠️ À CONNECTER À TON MOTEUR EXISTANT
 *
 * L'app a déjà un moteur par propriété. Pour le MVP "achat en cours",
 * on a besoin d'un snapshot annuel de base : revenus bruts + dépenses d'exploitation (sans dette).
 *
 * Si tu as déjà un calcul qui sort NOI/expenses/Income, remplace l'implémentation ici.
 *
 * @param {any} inputs
 */
export function getBaseSnapshot(inputs) {
  // Fallback minimal (si tu stockes déjà ces champs dans inputs_json)
  const grossIncomeAnnual = num(inputs?.grossIncomeAnnual, 0);
  const operatingExpensesAnnual = num(inputs?.operatingExpensesAnnual, 0);
  return { grossIncomeAnnual, operatingExpensesAnnual };
}

export function projectedValueAtYear(purchasePrice, yearsElapsed, appreciationAnnual) {
  return round2(purchasePrice * Math.pow(1 + appreciationAnnual, yearsElapsed));
}

export function projectedNOIAtYear({ base, yearsElapsed, assumptions, cfg }) {
  const rentGrowthAnnual = num(assumptions?.rentGrowthAnnual, 0);
  const expenseInflationAnnual = num(assumptions?.expenseInflationAnnual, 0);

  const income = base.grossIncomeAnnual * Math.pow(1 + rentGrowthAnnual, yearsElapsed);
  const opex = base.operatingExpensesAnnual * Math.pow(1 + expenseInflationAnnual, yearsElapsed);

  const vacancy = cfg.conservative.vacancyRate * income;
  const capex = cfg.conservative.capexReserveRate * income;

  return round2(income - vacancy - capex - opex);
}

function addYears(date, years) {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return d;
  }
  
  function toISODate(date) {
    return new Date(date).toISOString().slice(0, 10);
  }

  
function computeStatus({ dscr, cashOut, cfg, reasons }) {
  if (dscr == null || Number.isNaN(dscr)) return "DONNEES_INCOMPLETES";

  if (dscr < cfg.conservative.dscrMin) {
    reasons.push(`DSCR ${dscr.toFixed(2)} < ${cfg.conservative.dscrMin.toFixed(2)}`);
    return "BLOQUE_DSCR";
  }

  if (cashOut < cfg.refi.cashOutMinToConsider) {
    reasons.push(`Cash-out ${round2(cashOut)} < ${round2(cfg.refi.cashOutMinToConsider)}`);
    return "CASHOUT_FAIBLE";
  }

  return "OK";
}

/**
 * Comparateur de terme hypothécaire (3/5/7/10) à la date de renouvellement.
 * inputs: inputs_json de la propriété (achat en cours)
 * assumptions: { appreciationAnnual, rentGrowthAnnual, expenseInflationAnnual } en décimales (0.03)
 */
export function compareTerms({ inputs, cfg, assumptions }) {
  const base = getBaseSnapshot(inputs);

  const acquisition = new Date(inputs.acquisitionDate);
  const purchasePrice = num(inputs.purchasePrice, 0);
  const mortgageAmount = num(inputs.mortgageAmount, 0);
  const amortizationYears = num(inputs.amortizationYears, 25);

  const terms = /** @type {Array<3|5|7|10>} */ ([3, 5, 7, 10]);

  return terms.map((termYears) => {
    const reasons = [];

    const renewalDate = addYears(acquisition, termYears);
    const renewalDateISO = toISODate(renewalDate);


    const contractRate = num(inputs?.ratesByTerm?.[termYears], 0);
    if (!contractRate) reasons.push("Taux manquant");

    const mortgageBal = mortgageBalanceAfterYears({
      principal: mortgageAmount,
      ratePct: contractRate,
      amortizationYears,
      yearsElapsed: termYears,
      paymentFrequency: "monthly"
    });

    const valueAtRenewal = projectedValueAtYear(purchasePrice, termYears, num(assumptions?.appreciationAnnual, 0));

    const maxDebtRefi = round2(cfg.conservative.ltvMaxRefi * valueAtRenewal);
    const cashOut = round2(clampMin0(maxDebtRefi - mortgageBal));

    const stressRate = stressTestRatePct(contractRate, cfg);
    const remainingAmort = Math.max(1, amortizationYears - termYears);
    const amortForPayment = cfg.refi.amortizationMode === "RESET" ? cfg.refi.resetAmortizationYears : remainingAmort;

    // Conservateur: dette calculée sur "maxDebtRefi" (refi au max permis)
    const postRefiDebtService = annualDebtService({
      principal: maxDebtRefi,
      ratePct: stressRate,
      amortizationYears: amortForPayment,
      paymentFrequency: "monthly"
    });

    const noi = projectedNOIAtYear({ base, yearsElapsed: termYears, assumptions, cfg });
    const dscr = postRefiDebtService > 0 ? round2(noi / postRefiDebtService) : null;
    const cashflow = round2(noi - postRefiDebtService);

    const status = computeStatus({ dscr, cashOut, cfg, reasons });

    return {
      termYears,
      renewalDateISO,
      mortgageBalanceAtRenewal: mortgageBal,
      projectedValueAtRenewal: valueAtRenewal,
      maxDebtRefi,
      cashOutMax: cashOut,
      stressTestRate: round2(stressRate),
      postRefiAnnualDebtService: postRefiDebtService,
      noiAtRenewal: noi,
      dscrPostRefi: dscr,
      cashflowPostRefiAnnual: cashflow,
      status,
      reasons
    };
  });
}

export function pickBestTerm(rows, objective) {
  if (!rows?.length) return null;
  const ok = rows.filter((r) => r.status === "OK");
  const candidates = ok.length ? ok : rows;

  if (objective === "RECYCLE_RAPIDE") {
    // Max cash-out, tie-break: plus tôt
    return candidates
      .slice()
      .sort((a, b) => (b.cashOutMax - a.cashOutMax) || (a.termYears - b.termYears))[0];
  }

  // STABILITÉ: max cashflow, tie-break: taux stress test plus bas
  return candidates
    .slice()
    .sort((a, b) => (b.cashflowPostRefiAnnual - a.cashflowPostRefiAnnual) || (a.stressTestRate - b.stressTestRate))[0];
}
